import React, { useEffect, useState } from "react";
import { BeerStein, ForkKnife, Crown } from "@phosphor-icons/react";
import { api, money } from "../lib/api";
import { Offers } from "../components/Offers";

const GLASS_ICONS = {
  "Vaso largo": "https://static.prod-images.emergentagent.com/jobs/f4d02145-fcf7-41ef-be94-47907979e0b0/images/7aeed8d6cf40f55a2520bad98e28d8befbb89ceb97404d2ed1b3871b5e008999.png",
  "Copa tulipán": "https://static.prod-images.emergentagent.com/jobs/f4d02145-fcf7-41ef-be94-47907979e0b0/images/6d67d870f62d70a6a75105e8584ca8f7ed8ded98c6c54e32ef96ed95053206f1.png",
  "Vaso de yarda": "https://static.prod-images.emergentagent.com/jobs/f4d02145-fcf7-41ef-be94-47907979e0b0/images/a31440acee27df87a067a4ee37f125221c8e4cd330be6676f3f57e0ad94128d6.png",
};

export default function MenuRestaurante() {
  const [beers, setBeers] = useState([]);
  const [tastings, setTastings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadMenu = () => {
    setLoading(true);
    setError(false);
    api.get("/menu")
      .then((res) => {
        setBeers(res.data.beers || []);
        setTastings(res.data.tastings || []);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadMenu();
  }, []);

  return (
    <div data-testid="menu-page">
      {/* HERO */}
      <section className="relative grain overflow-hidden border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
          <div>
            <div className="inline-flex items-center gap-2 border border-[#D97736]/50 text-[#F5A623] px-3 py-1.5 mb-6">
              <Crown size={16} weight="fill" /> <span className="overline text-[#F5A623]">Restaurante Garaje</span>
            </div>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.9]">
              El menú donde la<br /><span className="text-[#D97736]">cerveza es la reina</span>
            </h1>
            <p className="text-gray-300 text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
              Aquí cada cerveza artesanal es la protagonista: resaltamos sus aromas y sabores, y la comida es el
              acompañante perfecto que realza cada sorbo. Elige tu cerveza y déjate llevar por lo que la acompaña.
            </p>
          </div>
        </div>
      </section>

      <Offers />

      {/* CERVEZAS + ACOMPAÑANTES */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20 space-y-16 md:space-y-24">
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="menu-loading">
            <div className="w-12 h-12 border-[3px] border-[#F5A623] border-t-transparent rounded-full animate-spin" />
            <p className="text-[#F5A623] mt-5 text-lg font-medium tracking-wide">Cargando el menú...</p>
            <p className="text-gray-400 mt-1 text-sm">Preparando nuestras cervezas y platos.</p>
          </div>
        )}
        {!loading && error && (
          <div className="text-center py-16 border border-white/10 bg-[#141414]" data-testid="menu-error">
            <p className="text-gray-300 mb-1">No pudimos cargar el menú.</p>
            <p className="text-gray-500 text-sm mb-6">Revisa tu conexión e intenta de nuevo.</p>
            <button
              onClick={loadMenu}
              className="px-6 py-2.5 bg-[#D97736] text-black font-semibold uppercase tracking-widest text-sm hover:bg-[#F5A623] transition-colors"
              data-testid="menu-retry"
            >
              Reintentar
            </button>
          </div>
        )}
        {!loading && !error && beers.map((item, i) => (
          <div
            key={item.id || i}
            className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}
            data-testid={`menu-beer-${i}`}
          >
            <div className="[direction:ltr] flex justify-center">
              <div className="relative">
                <div className="absolute -inset-6 bg-[#D97736]/10 blur-3xl rounded-full" />
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="relative h-64 md:h-96 w-auto object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.6)]"
                />
              </div>
            </div>

            <div className="[direction:ltr]">
              <div className="flex items-center gap-3 mb-2">
                <BeerStein size={22} weight="duotone" className="text-[#D97736]" />
                <span className="overline text-[#F5A623]">{item.abv ? `${item.abv} ABV · ` : ""}La protagonista</span>
              </div>
              <h2 className="font-display text-5xl md:text-6xl leading-none">{item.name}</h2>
              <p className="text-[#D97736] italic text-lg mt-2">{item.character}</p>
              {item.price != null && (
                <div className="mt-4 inline-flex items-baseline gap-2 border border-[#D97736]/40 px-4 py-2" data-testid="menu-beer-price">
                  <span className="overline text-gray-400">Precio</span>
                  <span className="font-display text-3xl text-[#F5A623]">{money(item.price)}</span>
                </div>
              )}
              {item.glass && (
                <div className="flex items-center gap-3 mt-4 text-gray-300 text-sm" data-testid="menu-glass">
                  {GLASS_ICONS[item.glass] && (
                    <img
                      src={GLASS_ICONS[item.glass]}
                      alt={item.glass}
                      className="w-9 h-12 object-contain shrink-0"
                      style={{ mixBlendMode: "lighten" }}
                    />
                  )}
                  <span>Se sirve en:<br /><span className="text-white font-medium text-base">{item.glass}</span></span>
                </div>
              )}

              {(item.dishes || []).length > 0 && (
                <div className="mt-8 border-t border-white/10 pt-6">
                  <div className="overline text-gray-400 flex items-center gap-2 mb-4">
                    <ForkKnife size={16} weight="fill" className="text-[#D97736]" /> La acompañan
                  </div>
                  <ul className="space-y-3">
                    {item.dishes.map((d, di) => {
                      const dName = typeof d === "string" ? d : d.name;
                      const dPrice = typeof d === "object" && d.price != null ? d.price : null;
                      return (
                        <li key={di} className="flex items-start gap-3 text-gray-200" data-testid="menu-dish">
                          <span className="mt-2 w-1.5 h-1.5 bg-[#D97736] rotate-45 shrink-0" />
                          <span className="text-base leading-snug flex-1">{dName}</span>
                          {dPrice != null && (
                            <span className="text-[#F5A623] font-medium text-base shrink-0" data-testid="menu-dish-price">{money(dPrice)}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}
      </section>

      {/* PLATOS DE DEGUSTACIÓN */}
      {tastings.length > 0 && (
        <section className="border-t border-white/10 bg-[#0d0d0d]">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
            <div className="text-center mb-12">
              <div className="overline text-[#F5A623] mb-3 flex items-center justify-center gap-2">
                <ForkKnife size={16} weight="fill" /> Para compartir
              </div>
              <h2 className="font-display text-5xl md:text-6xl leading-none">Platos de degustación</h2>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
                Ideales para acompañar tu cerveza y compartir en la mesa. Variedad de sabores en cada plato.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {tastings.map((t, i) => (
                <div
                  key={t.id || i}
                  className="bg-[#141414] border border-white/10 overflow-hidden group"
                  data-testid={`tasting-${i}`}
                >
                  <div className="h-52 overflow-hidden">
                    <img
                      src={t.image_url}
                      alt={t.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      style={{ transitionProperty: "transform" }}
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-2xl leading-tight">{t.name}</h3>
                      {(t.price_medium != null || t.price_large != null) ? (
                        <span className="text-right shrink-0" data-testid="tasting-price">
                          {t.price_medium != null && <span className="block font-display text-lg text-[#F5A623] leading-tight">Mediano {money(t.price_medium)}</span>}
                          {t.price_large != null && <span className="block font-display text-lg text-[#F5A623] leading-tight">Grande {money(t.price_large)}</span>}
                        </span>
                      ) : t.price != null ? (
                        <span className="font-display text-2xl text-[#F5A623] shrink-0" data-testid="tasting-price">{money(t.price)}</span>
                      ) : null}
                    </div>
                    <p className="text-gray-400 text-sm mt-2 leading-relaxed">{t.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NOTA FINAL */}
      <section className="border-t border-white/10 bg-[#0d0d0d]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 text-center">
          <p className="text-gray-400 text-sm">
            Menú del Restaurante Garaje · <span className="text-[#D97736]">Cervecería independiente</span>. Pregunta a
            nuestro equipo por las cervezas de temporada y el maridaje del día. 🍺
          </p>
        </div>
      </section>
    </div>
  );
}
